import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Chip,
  Link
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { Download as DownloadIcon, Refresh as RefreshIcon, LocalOffer as TagIcon, Business as BusinessIcon, AccessTime as QueueIcon } from '@mui/icons-material';
import { getConfig } from '../services/config';
import { startOfWeek, endOfWeek, format } from 'date-fns';

// Format seconds to HH:MM:SS
const formatDuration = (seconds) => {
  if (!seconds) return '0:00:00';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Generate ConnectWise ticket URL
const getTicketUrl = (config, ticketNumber) => {
  if (!config?.connectwiseUrl || !ticketNumber) return null;
  return `${config.connectwiseUrl}?routeTo=ServiceFXSR/${ticketNumber}`;
};

// Tab panel component
function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index} style={{ paddingTop: 16 }}>
      {value === index && children}
    </div>
  );
}

function Reports() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tabIndex, setTabIndex] = useState(0);
  const [startDate, setStartDate] = useState(startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [endDate, setEndDate] = useState(endOfWeek(new Date(), { weekStartsOn: 0 }));

  // Report data
  const [agentActivity, setAgentActivity] = useState(null);
  const [inboundDistribution, setInboundDistribution] = useState(null);
  const [outboundActivity, setOutboundActivity] = useState(null);
  const [callTags, setCallTags] = useState(null);
  const [clientStats, setClientStats] = useState(null);
  const [queueTime, setQueueTime] = useState(null);

  // Sorting state for each table
  const [agentSort, setAgentSort] = useState({ field: 'extension', direction: 'asc' });
  const [inboundSort, setInboundSort] = useState({ field: 'date', direction: 'desc' });
  const [outboundSort, setOutboundSort] = useState({ field: 'extension', direction: 'asc' });
  const [callTagsSort, setCallTagsSort] = useState({ field: 'call_started_at', direction: 'desc' });
  const [clientSort, setClientSort] = useState({ field: 'total_calls', direction: 'desc' });
  const [queueSort, setQueueSort] = useState({ field: 'total_queue_time_seconds', direction: 'desc' });
  const [callTagFilter, setCallTagFilter] = useState(null); // Filter for call tags by type

  // Sort handler
  const handleSort = (setter, currentSort, field) => {
    const isAsc = currentSort.field === field && currentSort.direction === 'asc';
    setter({ field, direction: isAsc ? 'desc' : 'asc' });
  };

  // Sort data helper
  const sortData = (data, sortConfig) => {
    if (!data) return data;
    return [...data].sort((a, b) => {
      let aVal = a[sortConfig.field];
      let bVal = b[sortConfig.field];

      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // Numeric comparison for numbers
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      // String comparison
      const comparison = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      return sortConfig.direction === 'asc' ? comparison : -comparison;
    });
  };

  useEffect(() => {
    getConfig().then(setConfig).catch(console.error);
  }, []);

  const fetchReport = async (reportType) => {
    if (!config) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        instance_id: config.instanceId,
        start_date: format(startDate, 'yyyy-MM-dd'),
        end_date: format(endDate, 'yyyy-MM-dd')
      });

      // Call tags use a different endpoint
      const endpoint = reportType === 'call-tags'
        ? `${config.apiUrl}/api/call-tags?${params}`
        : `${config.apiUrl}/api/reports/${reportType}?${params}`;

      const response = await fetch(endpoint);

      if (!response.ok) {
        throw new Error(`Failed to fetch report: ${response.statusText}`);
      }

      const data = await response.json();

      switch (reportType) {
        case 'agent-activity':
          setAgentActivity(data);
          break;
        case 'inbound-distribution':
          setInboundDistribution(data);
          break;
        case 'outbound-activity':
          setOutboundActivity(data);
          break;
        case 'call-tags':
          setCallTags(data);
          break;
        case 'client-stats':
          setClientStats(data);
          break;
        case 'queue-time':
          setQueueTime(data);
          break;
        default:
          break;
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
    // Fetch report for new tab if not loaded
    const reportTypes = ['agent-activity', 'inbound-distribution', 'outbound-activity', 'call-tags', 'client-stats', 'queue-time'];
    const reports = [agentActivity, inboundDistribution, outboundActivity, callTags, clientStats, queueTime];
    if (!reports[newValue]) {
      fetchReport(reportTypes[newValue]);
    }
  };

  const handleRefresh = () => {
    const reportTypes = ['agent-activity', 'inbound-distribution', 'outbound-activity', 'call-tags', 'client-stats', 'queue-time'];
    fetchReport(reportTypes[tabIndex]);
  };

  const handleDateChange = () => {
    // Clear cached reports when date range changes
    setAgentActivity(null);
    setInboundDistribution(null);
    setOutboundActivity(null);
    setCallTags(null);
    setClientStats(null);
    setQueueTime(null);
    // Fetch current tab's report
    const reportTypes = ['agent-activity', 'inbound-distribution', 'outbound-activity', 'call-tags', 'client-stats', 'queue-time'];
    fetchReport(reportTypes[tabIndex]);
  };

  const exportToCsv = (data, filename) => {
    if (!data || !data.agents) return;

    const headers = Object.keys(data.agents[0] || {});
    const csvContent = [
      headers.join(','),
      ...data.agents.map(row => headers.map(h => `"${row[h] || ''}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${format(startDate, 'yyyy-MM-dd')}_${format(endDate, 'yyyy-MM-dd')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Load first report on mount
  useEffect(() => {
    if (config && !agentActivity) {
      fetchReport('agent-activity');
    }
  }, [config]);

  if (!config) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Date Range Selector */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6">CDR Reports</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <DatePicker
            label="Start Date"
            value={startDate}
            onChange={setStartDate}
            slotProps={{ textField: { size: 'small' } }}
          />
          <DatePicker
            label="End Date"
            value={endDate}
            onChange={setEndDate}
            slotProps={{ textField: { size: 'small' } }}
          />
          <Button
            variant="contained"
            onClick={handleDateChange}
            disabled={loading}
          >
            Apply
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Report Tabs */}
      <Paper sx={{ width: '100%' }}>
        <Tabs value={tabIndex} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab label="Agent Activity" />
          <Tab label="Inbound Distribution" />
          <Tab label="Outbound Activity" />
          <Tab label="Call Tags" icon={<TagIcon fontSize="small" />} iconPosition="start" />
          <Tab label="Client Stats" icon={<BusinessIcon fontSize="small" />} iconPosition="start" />
          <Tab label="Queue Time" icon={<QueueIcon fontSize="small" />} iconPosition="start" />
        </Tabs>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Agent Activity Tab */}
        <TabPanel value={tabIndex} index={0}>
          {agentActivity && (
            <>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3, px: 2 }}>
                <Grid item xs={6} sm={2.4}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Total Calls</Typography>
                      <Typography variant="h4">{agentActivity.totals.total_calls}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Inbound Answered</Typography>
                      <Typography variant="h4">{agentActivity.totals.inbound_answered}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Inbound Missed</Typography>
                      <Typography variant="h4" color="error">{agentActivity.totals.inbound_missed}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Outbound</Typography>
                      <Typography variant="h4">{agentActivity.totals.outbound_calls}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={2.4}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Internal</Typography>
                      <Typography variant="h4">{agentActivity.totals.internal_calls}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, mb: 2 }}>
                <Button
                  startIcon={<DownloadIcon />}
                  onClick={() => exportToCsv(agentActivity, 'agent_activity')}
                >
                  Export CSV
                </Button>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={agentSort.field === 'extension' ? agentSort.direction : false}>
                        <TableSortLabel
                          active={agentSort.field === 'extension'}
                          direction={agentSort.field === 'extension' ? agentSort.direction : 'asc'}
                          onClick={() => handleSort(setAgentSort, agentSort, 'extension')}
                        >
                          Extension
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={agentSort.field === 'extension_name' ? agentSort.direction : false}>
                        <TableSortLabel
                          active={agentSort.field === 'extension_name'}
                          direction={agentSort.field === 'extension_name' ? agentSort.direction : 'asc'}
                          onClick={() => handleSort(setAgentSort, agentSort, 'extension_name')}
                        >
                          Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={agentSort.field === 'inbound_answered' ? agentSort.direction : false}>
                        <TableSortLabel
                          active={agentSort.field === 'inbound_answered'}
                          direction={agentSort.field === 'inbound_answered' ? agentSort.direction : 'asc'}
                          onClick={() => handleSort(setAgentSort, agentSort, 'inbound_answered')}
                        >
                          Inbound Answered
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={agentSort.field === 'inbound_missed' ? agentSort.direction : false}>
                        <TableSortLabel
                          active={agentSort.field === 'inbound_missed'}
                          direction={agentSort.field === 'inbound_missed' ? agentSort.direction : 'asc'}
                          onClick={() => handleSort(setAgentSort, agentSort, 'inbound_missed')}
                        >
                          Inbound Missed
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={agentSort.field === 'outbound_calls' ? agentSort.direction : false}>
                        <TableSortLabel
                          active={agentSort.field === 'outbound_calls'}
                          direction={agentSort.field === 'outbound_calls' ? agentSort.direction : 'asc'}
                          onClick={() => handleSort(setAgentSort, agentSort, 'outbound_calls')}
                        >
                          Outbound
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={agentSort.field === 'internal_calls' ? agentSort.direction : false}>
                        <TableSortLabel
                          active={agentSort.field === 'internal_calls'}
                          direction={agentSort.field === 'internal_calls' ? agentSort.direction : 'asc'}
                          onClick={() => handleSort(setAgentSort, agentSort, 'internal_calls')}
                        >
                          Internal
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={agentSort.field === 'total_calls' ? agentSort.direction : false}>
                        <TableSortLabel
                          active={agentSort.field === 'total_calls'}
                          direction={agentSort.field === 'total_calls' ? agentSort.direction : 'asc'}
                          onClick={() => handleSort(setAgentSort, agentSort, 'total_calls')}
                        >
                          Total
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={agentSort.field === 'total_talk_time_seconds' ? agentSort.direction : false}>
                        <TableSortLabel
                          active={agentSort.field === 'total_talk_time_seconds'}
                          direction={agentSort.field === 'total_talk_time_seconds' ? agentSort.direction : 'asc'}
                          onClick={() => handleSort(setAgentSort, agentSort, 'total_talk_time_seconds')}
                        >
                          Talk Time
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={agentSort.field === 'avg_duration_seconds' ? agentSort.direction : false}>
                        <TableSortLabel
                          active={agentSort.field === 'avg_duration_seconds'}
                          direction={agentSort.field === 'avg_duration_seconds' ? agentSort.direction : 'asc'}
                          onClick={() => handleSort(setAgentSort, agentSort, 'avg_duration_seconds')}
                        >
                          Avg Duration
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortData(agentActivity.agents, agentSort).map((agent) => (
                      <TableRow key={agent.extension}>
                        <TableCell>{agent.extension}</TableCell>
                        <TableCell>{agent.extension_name || '-'}</TableCell>
                        <TableCell align="right">{agent.inbound_answered}</TableCell>
                        <TableCell align="right" sx={{ color: agent.inbound_missed > 0 ? 'error.main' : 'inherit' }}>
                          {agent.inbound_missed}
                        </TableCell>
                        <TableCell align="right">{agent.outbound_calls}</TableCell>
                        <TableCell align="right">{agent.internal_calls}</TableCell>
                        <TableCell align="right">{agent.total_calls}</TableCell>
                        <TableCell align="right">{formatDuration(agent.total_talk_time_seconds)}</TableCell>
                        <TableCell align="right">{agent.avg_duration_seconds}s</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </TabPanel>

        {/* Inbound Distribution Tab */}
        <TabPanel value={tabIndex} index={1}>
          {inboundDistribution && (
            <>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3, px: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Total Inbound</Typography>
                      <Typography variant="h4">{inboundDistribution.totals.total_inbound}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Answered</Typography>
                      <Typography variant="h4" color="success.main">{inboundDistribution.totals.answered}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Missed</Typography>
                      <Typography variant="h4" color="error">{inboundDistribution.totals.missed}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Answer Rate</Typography>
                      <Typography variant="h4">{inboundDistribution.totals.answer_rate_percent}%</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ px: 2, mb: 2 }}>Daily Summary</Typography>
              <TableContainer sx={{ mb: 4 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={inboundSort.field === 'date' ? inboundSort.direction : false}>
                        <TableSortLabel
                          active={inboundSort.field === 'date'}
                          direction={inboundSort.field === 'date' ? inboundSort.direction : 'asc'}
                          onClick={() => handleSort(setInboundSort, inboundSort, 'date')}
                        >
                          Date
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={inboundSort.field === 'total_inbound' ? inboundSort.direction : false}>
                        <TableSortLabel
                          active={inboundSort.field === 'total_inbound'}
                          direction={inboundSort.field === 'total_inbound' ? inboundSort.direction : 'asc'}
                          onClick={() => handleSort(setInboundSort, inboundSort, 'total_inbound')}
                        >
                          Total Inbound
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={inboundSort.field === 'answered' ? inboundSort.direction : false}>
                        <TableSortLabel
                          active={inboundSort.field === 'answered'}
                          direction={inboundSort.field === 'answered' ? inboundSort.direction : 'asc'}
                          onClick={() => handleSort(setInboundSort, inboundSort, 'answered')}
                        >
                          Answered
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={inboundSort.field === 'missed' ? inboundSort.direction : false}>
                        <TableSortLabel
                          active={inboundSort.field === 'missed'}
                          direction={inboundSort.field === 'missed' ? inboundSort.direction : 'asc'}
                          onClick={() => handleSort(setInboundSort, inboundSort, 'missed')}
                        >
                          Missed
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={inboundSort.field === 'answer_rate_percent' ? inboundSort.direction : false}>
                        <TableSortLabel
                          active={inboundSort.field === 'answer_rate_percent'}
                          direction={inboundSort.field === 'answer_rate_percent' ? inboundSort.direction : 'asc'}
                          onClick={() => handleSort(setInboundSort, inboundSort, 'answer_rate_percent')}
                        >
                          Answer Rate
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortData(inboundDistribution.daily_summary, inboundSort).map((day) => (
                      <TableRow key={day.date}>
                        <TableCell>{day.date}</TableCell>
                        <TableCell align="right">{day.total_inbound}</TableCell>
                        <TableCell align="right">{day.answered}</TableCell>
                        <TableCell align="right" sx={{ color: day.missed > 0 ? 'error.main' : 'inherit' }}>
                          {day.missed}
                        </TableCell>
                        <TableCell align="right">{day.answer_rate_percent}%</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </TabPanel>

        {/* Outbound Activity Tab */}
        <TabPanel value={tabIndex} index={2}>
          {outboundActivity && (
            <>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3, px: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Total Attempts</Typography>
                      <Typography variant="h4">{outboundActivity.totals.total_attempts}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Connected</Typography>
                      <Typography variant="h4" color="success.main">{outboundActivity.totals.connected_calls}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Connect Rate</Typography>
                      <Typography variant="h4">{outboundActivity.totals.connect_rate_percent}%</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Total Talk Time</Typography>
                      <Typography variant="h4">{formatDuration(outboundActivity.totals.total_talk_time_seconds)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', px: 2, mb: 2 }}>
                <Button
                  startIcon={<DownloadIcon />}
                  onClick={() => exportToCsv(outboundActivity, 'outbound_activity')}
                >
                  Export CSV
                </Button>
              </Box>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={outboundSort.field === 'extension' ? outboundSort.direction : false}>
                        <TableSortLabel
                          active={outboundSort.field === 'extension'}
                          direction={outboundSort.field === 'extension' ? outboundSort.direction : 'asc'}
                          onClick={() => handleSort(setOutboundSort, outboundSort, 'extension')}
                        >
                          Extension
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={outboundSort.field === 'extension_name' ? outboundSort.direction : false}>
                        <TableSortLabel
                          active={outboundSort.field === 'extension_name'}
                          direction={outboundSort.field === 'extension_name' ? outboundSort.direction : 'asc'}
                          onClick={() => handleSort(setOutboundSort, outboundSort, 'extension_name')}
                        >
                          Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={outboundSort.field === 'total_attempts' ? outboundSort.direction : false}>
                        <TableSortLabel
                          active={outboundSort.field === 'total_attempts'}
                          direction={outboundSort.field === 'total_attempts' ? outboundSort.direction : 'asc'}
                          onClick={() => handleSort(setOutboundSort, outboundSort, 'total_attempts')}
                        >
                          Total Attempts
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={outboundSort.field === 'connected_calls' ? outboundSort.direction : false}>
                        <TableSortLabel
                          active={outboundSort.field === 'connected_calls'}
                          direction={outboundSort.field === 'connected_calls' ? outboundSort.direction : 'asc'}
                          onClick={() => handleSort(setOutboundSort, outboundSort, 'connected_calls')}
                        >
                          Connected
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={outboundSort.field === 'connect_rate_percent' ? outboundSort.direction : false}>
                        <TableSortLabel
                          active={outboundSort.field === 'connect_rate_percent'}
                          direction={outboundSort.field === 'connect_rate_percent' ? outboundSort.direction : 'asc'}
                          onClick={() => handleSort(setOutboundSort, outboundSort, 'connect_rate_percent')}
                        >
                          Connect Rate
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={outboundSort.field === 'total_talk_time_seconds' ? outboundSort.direction : false}>
                        <TableSortLabel
                          active={outboundSort.field === 'total_talk_time_seconds'}
                          direction={outboundSort.field === 'total_talk_time_seconds' ? outboundSort.direction : 'asc'}
                          onClick={() => handleSort(setOutboundSort, outboundSort, 'total_talk_time_seconds')}
                        >
                          Talk Time
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={outboundSort.field === 'avg_duration_seconds' ? outboundSort.direction : false}>
                        <TableSortLabel
                          active={outboundSort.field === 'avg_duration_seconds'}
                          direction={outboundSort.field === 'avg_duration_seconds' ? outboundSort.direction : 'asc'}
                          onClick={() => handleSort(setOutboundSort, outboundSort, 'avg_duration_seconds')}
                        >
                          Avg Duration
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortData(outboundActivity.agents, outboundSort).map((agent) => (
                      <TableRow key={agent.extension}>
                        <TableCell>{agent.extension}</TableCell>
                        <TableCell>{agent.extension_name || '-'}</TableCell>
                        <TableCell align="right">{agent.total_attempts}</TableCell>
                        <TableCell align="right">{agent.connected_calls}</TableCell>
                        <TableCell align="right">{agent.connect_rate_percent}%</TableCell>
                        <TableCell align="right">{formatDuration(agent.total_talk_time_seconds)}</TableCell>
                        <TableCell align="right">{agent.avg_duration_seconds}s</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </TabPanel>

        {/* Call Tags Tab */}
        <TabPanel value={tabIndex} index={3}>
          {callTags && (
            <>
              {/* Summary Cards - Clickable to filter */}
              <Grid container spacing={2} sx={{ mb: 3, px: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Card
                    sx={{
                      cursor: 'pointer',
                      border: callTagFilter === null ? 2 : 1,
                      borderColor: callTagFilter === null ? 'primary.main' : 'divider',
                      '&:hover': { backgroundColor: 'action.hover' }
                    }}
                    onClick={() => setCallTagFilter(null)}
                  >
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Total Tagged</Typography>
                      <Typography variant="h4">
                        {callTags.summary?.reduce((sum, s) => sum + s.count, 0) || 0}
                      </Typography>
                      {callTagFilter === null && (
                        <Chip label="Showing All" size="small" color="primary" sx={{ mt: 1 }} />
                      )}
                    </CardContent>
                  </Card>
                </Grid>
                {callTags.summary?.map((item) => (
                  <Grid item xs={6} sm={3} key={item.tag_type}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: callTagFilter === item.tag_type ? 2 : 1,
                        borderColor: callTagFilter === item.tag_type ? 'primary.main' : 'divider',
                        '&:hover': { backgroundColor: 'action.hover' }
                      }}
                      onClick={() => setCallTagFilter(callTagFilter === item.tag_type ? null : item.tag_type)}
                    >
                      <CardContent>
                        <Typography color="textSecondary" gutterBottom>{item.tag_label}</Typography>
                        <Typography variant="h4">{item.count}</Typography>
                        {callTagFilter === item.tag_type && (
                          <Chip label="Filtered" size="small" color="primary" sx={{ mt: 1 }} />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>

              {/* Top 5 Clients Section */}
              {callTags.top_clients && callTags.top_clients.length > 0 && (
                <Box sx={{ px: 2, mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>Top 5 Clients by Tagged Calls</Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'action.hover' }}>
                          <TableCell>Company</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell align="right">New Ticket</TableCell>
                          <TableCell align="right">Follow Up</TableCell>
                          <TableCell align="right">Returning Tech</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {callTags.top_clients.map((client, idx) => (
                          <TableRow key={client.company}>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: idx === 0 ? 'bold' : 'normal',
                                    color: idx === 0 ? 'primary.main' : 'inherit'
                                  }}
                                >
                                  {idx + 1}. {client.company}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Chip label={client.count} size="small" />
                            </TableCell>
                            <TableCell align="right">
                              {client.new_ticket > 0 ? (
                                <Chip label={client.new_ticket} size="small" color="primary" variant="outlined" />
                              ) : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {client.ticket_follow_up > 0 ? (
                                <Chip label={client.ticket_follow_up} size="small" color="warning" variant="outlined" />
                              ) : '-'}
                            </TableCell>
                            <TableCell align="right">
                              {client.returning_tech_call > 0 ? (
                                <Chip label={client.returning_tech_call} size="small" color="success" variant="outlined" />
                              ) : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              <Typography variant="h6" sx={{ px: 2, mb: 2 }}>
                Tagged Calls
                {callTagFilter && (
                  <Chip
                    label={`Filtering: ${callTags.summary?.find(s => s.tag_type === callTagFilter)?.tag_label || callTagFilter}`}
                    onDelete={() => setCallTagFilter(null)}
                    size="small"
                    color="primary"
                    sx={{ ml: 2 }}
                  />
                )}
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={callTagsSort.field === 'call_started_at' ? callTagsSort.direction : false}>
                        <TableSortLabel
                          active={callTagsSort.field === 'call_started_at'}
                          direction={callTagsSort.field === 'call_started_at' ? callTagsSort.direction : 'asc'}
                          onClick={() => handleSort(setCallTagsSort, callTagsSort, 'call_started_at')}
                        >
                          Date/Time
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={callTagsSort.field === 'extension' ? callTagsSort.direction : false}>
                        <TableSortLabel
                          active={callTagsSort.field === 'extension'}
                          direction={callTagsSort.field === 'extension' ? callTagsSort.direction : 'asc'}
                          onClick={() => handleSort(setCallTagsSort, callTagsSort, 'extension')}
                        >
                          Extension
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={callTagsSort.field === 'caller_number' ? callTagsSort.direction : false}>
                        <TableSortLabel
                          active={callTagsSort.field === 'caller_number'}
                          direction={callTagsSort.field === 'caller_number' ? callTagsSort.direction : 'asc'}
                          onClick={() => handleSort(setCallTagsSort, callTagsSort, 'caller_number')}
                        >
                          Caller
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={callTagsSort.field === 'ticket_number' ? callTagsSort.direction : false}>
                        <TableSortLabel
                          active={callTagsSort.field === 'ticket_number'}
                          direction={callTagsSort.field === 'ticket_number' ? callTagsSort.direction : 'asc'}
                          onClick={() => handleSort(setCallTagsSort, callTagsSort, 'ticket_number')}
                        >
                          Ticket
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={callTagsSort.field === 'ticket_company' ? callTagsSort.direction : false}>
                        <TableSortLabel
                          active={callTagsSort.field === 'ticket_company'}
                          direction={callTagsSort.field === 'ticket_company' ? callTagsSort.direction : 'asc'}
                          onClick={() => handleSort(setCallTagsSort, callTagsSort, 'ticket_company')}
                        >
                          Client
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={callTagsSort.field === 'tag_type' ? callTagsSort.direction : false}>
                        <TableSortLabel
                          active={callTagsSort.field === 'tag_type'}
                          direction={callTagsSort.field === 'tag_type' ? callTagsSort.direction : 'asc'}
                          onClick={() => handleSort(setCallTagsSort, callTagsSort, 'tag_type')}
                        >
                          Tag
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={callTagsSort.field === 'tagged_by' ? callTagsSort.direction : false}>
                        <TableSortLabel
                          active={callTagsSort.field === 'tagged_by'}
                          direction={callTagsSort.field === 'tagged_by' ? callTagsSort.direction : 'asc'}
                          onClick={() => handleSort(setCallTagsSort, callTagsSort, 'tagged_by')}
                        >
                          Tagged By
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortData(callTags.tags?.filter(t => !callTagFilter || t.tag_type === callTagFilter), callTagsSort)?.map((tag) => (
                      <TableRow key={tag.id}>
                        <TableCell>
                          {new Date(tag.call_started_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </TableCell>
                        <TableCell>
                          {tag.extension}
                          {tag.extension_name && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {tag.extension_name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {tag.caller_number || '-'}
                          {tag.caller_name && tag.caller_name !== tag.caller_number && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {tag.caller_name}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {tag.ticket_number ? (
                            getTicketUrl(config, tag.ticket_number) ? (
                              <Link
                                href={getTicketUrl(config, tag.ticket_number)}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ textDecoration: 'none' }}
                              >
                                <Chip
                                  label={`#${tag.ticket_number}`}
                                  size="small"
                                  color="success"
                                  clickable
                                  title="Click to open in ConnectWise"
                                />
                              </Link>
                            ) : (
                              `#${tag.ticket_number}`
                            )
                          ) : (
                            <Typography color="text.disabled">N/A</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {tag.ticket_company || <Typography color="text.disabled">N/A</Typography>}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={
                              tag.tag_type === 'new_ticket' ? 'New Ticket' :
                              tag.tag_type === 'ticket_follow_up' ? 'Ticket Follow Up' :
                              tag.tag_type === 'returning_tech_call' ? 'Returning Tech Call' :
                              tag.tag_type
                            }
                            size="small"
                            color={
                              tag.tag_type === 'new_ticket' ? 'primary' :
                              tag.tag_type === 'ticket_follow_up' ? 'warning' :
                              tag.tag_type === 'returning_tech_call' ? 'success' :
                              'default'
                            }
                          />
                        </TableCell>
                        <TableCell>{tag.tagged_by}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {(!callTags.tags || callTags.tags.length === 0) && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <TagIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">No tagged calls in this date range</Typography>
                </Box>
              )}
            </>
          )}
        </TabPanel>

        {/* Client Stats Tab */}
        <TabPanel value={tabIndex} index={4}>
          {clientStats && (
            <>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3, px: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Total Clients</Typography>
                      <Typography variant="h4">{clientStats.totals?.total_clients || 0}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Tagged Calls</Typography>
                      <Typography variant="h4">{clientStats.totals?.total_calls || 0}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Total Tickets</Typography>
                      <Typography variant="h4">{clientStats.totals?.total_tickets || 0}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Talk Time</Typography>
                      <Typography variant="h4">{formatDuration(clientStats.totals?.total_talk_time_seconds || 0)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ px: 2, mb: 2 }}>Calls by ConnectWise Client</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={clientSort.field === 'company' ? clientSort.direction : false}>
                        <TableSortLabel
                          active={clientSort.field === 'company'}
                          direction={clientSort.field === 'company' ? clientSort.direction : 'asc'}
                          onClick={() => handleSort(setClientSort, clientSort, 'company')}
                        >
                          Company
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={clientSort.field === 'total_calls' ? clientSort.direction : false}>
                        <TableSortLabel
                          active={clientSort.field === 'total_calls'}
                          direction={clientSort.field === 'total_calls' ? clientSort.direction : 'asc'}
                          onClick={() => handleSort(setClientSort, clientSort, 'total_calls')}
                        >
                          Calls
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={clientSort.field === 'total_tickets' ? clientSort.direction : false}>
                        <TableSortLabel
                          active={clientSort.field === 'total_tickets'}
                          direction={clientSort.field === 'total_tickets' ? clientSort.direction : 'asc'}
                          onClick={() => handleSort(setClientSort, clientSort, 'total_tickets')}
                        >
                          Tickets
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={clientSort.field === 'total_talk_time_seconds' ? clientSort.direction : false}>
                        <TableSortLabel
                          active={clientSort.field === 'total_talk_time_seconds'}
                          direction={clientSort.field === 'total_talk_time_seconds' ? clientSort.direction : 'asc'}
                          onClick={() => handleSort(setClientSort, clientSort, 'total_talk_time_seconds')}
                        >
                          Talk Time
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={clientSort.field === 'avg_duration_seconds' ? clientSort.direction : false}>
                        <TableSortLabel
                          active={clientSort.field === 'avg_duration_seconds'}
                          direction={clientSort.field === 'avg_duration_seconds' ? clientSort.direction : 'asc'}
                          onClick={() => handleSort(setClientSort, clientSort, 'avg_duration_seconds')}
                        >
                          Avg Duration
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right">Agents</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortData(clientStats.clients, clientSort)?.map((client) => (
                      <TableRow key={client.company}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {client.company}
                          </Typography>
                          {client.ticket_numbers?.length > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              Tickets: {client.ticket_numbers.slice(0, 3).join(', ')}
                              {client.ticket_numbers.length > 3 && `... +${client.ticket_numbers.length - 3} more`}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">{client.total_calls}</TableCell>
                        <TableCell align="right">{client.total_tickets}</TableCell>
                        <TableCell align="right">{formatDuration(client.total_talk_time_seconds)}</TableCell>
                        <TableCell align="right">{client.avg_duration_seconds}s</TableCell>
                        <TableCell align="right">{client.unique_extensions}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {(!clientStats.clients || clientStats.clients.length === 0) && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <BusinessIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">No client data in this date range</Typography>
                  <Typography variant="body2" color="text.disabled">
                    Tag calls with CW tickets to see client stats
                  </Typography>
                </Box>
              )}
            </>
          )}
        </TabPanel>

        {/* Queue Time Tab */}
        <TabPanel value={tabIndex} index={5}>
          {queueTime && (
            <>
              {/* Summary Cards */}
              <Grid container spacing={2} sx={{ mb: 3, px: 2 }}>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Total Logins</Typography>
                      <Typography variant="h4">{queueTime.totals?.total_logins || 0}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Total Logouts</Typography>
                      <Typography variant="h4">{queueTime.totals?.total_logouts || 0}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Queue Time</Typography>
                      <Typography variant="h4">{formatDuration(queueTime.totals?.total_queue_time_seconds || 0)}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Card>
                    <CardContent>
                      <Typography color="textSecondary" gutterBottom>Agents</Typography>
                      <Typography variant="h4">{queueTime.totals?.total_agents || 0}</Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Typography variant="h6" sx={{ px: 2, mb: 2 }}>Queue Time by Agent</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sortDirection={queueSort.field === 'extension' ? queueSort.direction : false}>
                        <TableSortLabel
                          active={queueSort.field === 'extension'}
                          direction={queueSort.field === 'extension' ? queueSort.direction : 'asc'}
                          onClick={() => handleSort(setQueueSort, queueSort, 'extension')}
                        >
                          Extension
                        </TableSortLabel>
                      </TableCell>
                      <TableCell sortDirection={queueSort.field === 'extension_name' ? queueSort.direction : false}>
                        <TableSortLabel
                          active={queueSort.field === 'extension_name'}
                          direction={queueSort.field === 'extension_name' ? queueSort.direction : 'asc'}
                          onClick={() => handleSort(setQueueSort, queueSort, 'extension_name')}
                        >
                          Name
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={queueSort.field === 'login_count' ? queueSort.direction : false}>
                        <TableSortLabel
                          active={queueSort.field === 'login_count'}
                          direction={queueSort.field === 'login_count' ? queueSort.direction : 'asc'}
                          onClick={() => handleSort(setQueueSort, queueSort, 'login_count')}
                        >
                          Logins
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={queueSort.field === 'logout_count' ? queueSort.direction : false}>
                        <TableSortLabel
                          active={queueSort.field === 'logout_count'}
                          direction={queueSort.field === 'logout_count' ? queueSort.direction : 'asc'}
                          onClick={() => handleSort(setQueueSort, queueSort, 'logout_count')}
                        >
                          Logouts
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={queueSort.field === 'total_queue_time_seconds' ? queueSort.direction : false}>
                        <TableSortLabel
                          active={queueSort.field === 'total_queue_time_seconds'}
                          direction={queueSort.field === 'total_queue_time_seconds' ? queueSort.direction : 'asc'}
                          onClick={() => handleSort(setQueueSort, queueSort, 'total_queue_time_seconds')}
                        >
                          Queue Time
                        </TableSortLabel>
                      </TableCell>
                      <TableCell align="right" sortDirection={queueSort.field === 'avg_session_seconds' ? queueSort.direction : false}>
                        <TableSortLabel
                          active={queueSort.field === 'avg_session_seconds'}
                          direction={queueSort.field === 'avg_session_seconds' ? queueSort.direction : 'asc'}
                          onClick={() => handleSort(setQueueSort, queueSort, 'avg_session_seconds')}
                        >
                          Avg Session
                        </TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortData(queueTime.agents, queueSort)?.map((agent) => (
                      <TableRow key={agent.extension}>
                        <TableCell>{agent.extension}</TableCell>
                        <TableCell>{agent.extension_name || '-'}</TableCell>
                        <TableCell align="right">{agent.login_count}</TableCell>
                        <TableCell align="right">{agent.logout_count}</TableCell>
                        <TableCell align="right">{formatDuration(agent.total_queue_time_seconds)}</TableCell>
                        <TableCell align="right">{formatDuration(Math.round(agent.avg_session_seconds))}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {(!queueTime.agents || queueTime.agents.length === 0) && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <QueueIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography color="text.secondary">No queue activity in this date range</Typography>
                  <Typography variant="body2" color="text.disabled">
                    Queue events (*62/*63) will appear here
                  </Typography>
                </Box>
              )}
            </>
          )}
        </TabPanel>
      </Paper>
    </Container>
  );
}

export default Reports;
